/**
 * Base Node Configuration Types
 * Defines the structure for creating nodes with BaseNode component
 */

import type { ReactNode } from 'react';

// ============================================================================
// Connector Types
// ============================================================================

export type ConnectorType = 'text' | 'image' | 'video' | 'audio' | 'any';

export interface ConnectorConfig {
  id: string;
  type: ConnectorType;
  label: string;
  position: 'top' | 'middle' | 'bottom';
  required?: boolean;
  accept?: ConnectorType[]; // For validation - what types can connect
}

export interface ConnectorDefinition {
  inputs: ConnectorConfig[];
  outputs: ConnectorConfig[];
}

// ============================================================================
// Node Configuration
// ============================================================================

export type NodeVariant = 'positive' | 'loader' | 'output' | 'panel';

export interface NodeHeaderConfig {
  icon?: ReactNode;
  title: string;
  description?: string;        // Optional description for info tooltip
  variant: NodeVariant;
  outputType?: ConnectorType;   // Output media type for type-based coloring
  actions?: ReactNode;
  backgroundColor?: string;  // Custom background color
  textColor?: string;         // Custom text color
}

export interface NodeStatusBarConfig {
  show: boolean;
  status?: 'idle' | 'processing' | 'error' | 'success' | 'warning';
  message?: string;
  progress?: number; // 0-100
  showProgress?: boolean;
}

export interface NodeBodySection {
  id: string;
  label?: string;
  collapsible?: boolean;
  collapsed?: boolean;
  content: ReactNode;
}

export interface NodeConfig {
  // Header configuration
  header: NodeHeaderConfig;
  
  // Status bar configuration
  statusBar?: NodeStatusBarConfig;
  
  // Connector configuration
  connectors?: ConnectorDefinition;
  
  // Body sections (helper, details, controls, etc.)
  sections?: NodeBodySection[];
  
  // Resize configuration
  resizable?: boolean;
  minWidth?: number;
  minHeight?: number;
  
  // Styling
  className?: string;
  
  // Error state
  error?: string | null;
}

// ============================================================================
// Connector Metadata (for visual display)
// ============================================================================

export interface ConnectorMetadata {
  color: string;
  icon: string;
  label: string;
}

export const CONNECTOR_METADATA: Record<ConnectorType, ConnectorMetadata> = {
  text: {
    color: 'var(--color-type-text)', // Green from CSS variables
    icon: '📝',
    label: 'Text'
  },
  image: {
    color: 'var(--color-type-image)', // Orange from CSS variables
    icon: '🖼️',
    label: 'Image'
  },
  video: {
    color: 'var(--color-type-video)', // Purple from CSS variables
    icon: '🎬',
    label: 'Video'
  },
  audio: {
    color: 'var(--color-type-audio)', // Green from CSS variables
    icon: '🎙️',
    label: 'Audio'
  },
  any: {
    color: 'var(--color-type-any)', // Gray from CSS variables
    icon: '⚡',
    label: 'Any'
  }
};

/**
 * Edge stroke colors by connector type — single source of truth for React Flow
 * edge coloring. Hardcoded hex (not CSS vars) because these are applied as inline
 * SVG stroke styles on edges. Mirrors the --color-type-* tokens in _variables.css.
 */
export const EDGE_COLOR_MAP: Record<string, string> = {
  text: '#FED402',
  image: '#42C3D7',
  video: '#FF4B42',
  audio: '#10B981',
  any: '#6B7280',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two connector types are compatible
 */
export function areConnectorsCompatible(
  sourceType: ConnectorType,
  targetType: ConnectorType
): boolean {
  // 'any' can connect to anything
  if (sourceType === 'any' || targetType === 'any') {
    return true;
  }
  
  // Otherwise, types must match
  return sourceType === targetType;
}

/**
 * Validate a connection based on connector configs
 */
export function validateConnection(
  sourceConnector: ConnectorConfig,
  targetConnector: ConnectorConfig
): boolean {
  // Check if target accepts the source type
  if (targetConnector.accept && targetConnector.accept.length > 0) {
    return targetConnector.accept.includes(sourceConnector.type);
  }
  
  // Fall back to basic compatibility check
  return areConnectorsCompatible(sourceConnector.type, targetConnector.type);
}
